'use client';

import { c as _c } from "react/compiler-runtime";
import { Loader2, Search, Sparkles } from 'lucide-react';
import z from 'zod';
import { useI18n } from '~/features/i18n';
import { cn } from '~/utils';
import { useAppForm } from '~/features/form/hooks';
import { useActivityAiActiveChat } from '../useActivityAiActiveChat';
const AI_PROMPT_VALIDATOR = z.object({
  prompt: z.string().trim().min(1)
});

/**
 * Mobile-only sticky-bottom composer rendered alongside {@link ActivityAiMobileLanding}.
 * Submitting sends into the active chat; the landing (and this composer) unmount as soon
 * as the chat store has any messages.
 */
export const ActivityAiMobileLandingComposer: React.FC = () => {
  const $ = _c(30);
  const i18n = useI18n();
  const aiChat = useActivityAiActiveChat();
  const isStreaming = aiChat.status === "submitted" || aiChat.status === "streaming";
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      prompt: ""
    };
    t1 = {
      onChange: AI_PROMPT_VALIDATOR,
      onSubmit: AI_PROMPT_VALIDATOR
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== aiChat) {
    t2 = {
      defaultValues: t0,
      validators: t1,
      onSubmit(t3) {
        const {
          value
        } = t3;
        aiChat.sendMessage(value.prompt);
      }
    };
    $[2] = aiChat;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const form = useAppForm(t2);
  let t3;
  if ($[4] !== form) {
    t3 = e => {
      e.preventDefault();
      return form.handleSubmit();
    };
    $[4] = form;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("sticky inset-x-0 bottom-0 z-modals rounded-t-3xl border-t border-stone-200/70 bg-white px-4 pt-3 shadow-[0_-4px_16px_rgba(0,0,0,0.04)] lg:hidden", "pb-[max(env(safe-area-inset-bottom),0.75rem)]");
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== i18n) {
    t5 = i18n._("activity.search.ai.promptLabel", undefined, {
      message: "Describe your team building"
    });
    $[7] = i18n;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <label className="sr-only" htmlFor="activity-ai-mobile-landing-prompt">{t5}</label>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Sparkles className="size-4 shrink-0 text-stone-500" strokeWidth={2.25} aria-hidden={true} />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== i18n) {
    t8 = field => <input id="activity-ai-mobile-landing-prompt" name={field.name} value={field.state.value} onChange={e_0 => field.handleChange(e_0.target.value)} onBlur={field.handleBlur} placeholder={i18n._("activity.search.ai.mobilePlaceholder", undefined, {
      message: "Stel je vraag\u2026"
    })} className="min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-stone-800 outline-none ring-0 placeholder:text-stone-400 focus:ring-0" autoComplete="off" enterKeyHint="send" />;
    $[12] = i18n;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== form.Field || $[15] !== t8) {
    t9 = <form.Field name="prompt">{t8}</form.Field>;
    $[14] = form.Field;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== i18n || $[18] !== isStreaming) {
    t10 = canSubmit => <button type="submit" disabled={!canSubmit || isStreaming} aria-label={i18n._("activity.search.ai.submit", undefined, {
      message: "Search team building"
    })} className={cn("flex size-9 shrink-0 items-center justify-center rounded-full text-white transition", canSubmit && !isStreaming ? "bg-tertiary hover:bg-tertiary-700 active:scale-95" : "cursor-not-allowed bg-tertiary/30")}>{isStreaming ? <Loader2 className="size-4 animate-spin" strokeWidth={2.25} aria-hidden={true} /> : <Search className="size-4" strokeWidth={2.25} />}</button>;
    $[17] = i18n;
    $[18] = isStreaming;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== form.Subscribe || $[21] !== t10) {
    t11 = <form.Subscribe selector={_temp}>{t10}</form.Subscribe>;
    $[20] = form.Subscribe;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t9) {
    t12 = <div className="flex items-center gap-2 rounded-full bg-stone-100 py-1.5 pl-4 pr-1.5">{t7}{t9}{t11}</div>;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t3 || $[28] !== t6) {
    t13 = <form onSubmit={t3} className={t4}>{t6}{t12}</form>;
    $[26] = t12;
    $[27] = t3;
    $[28] = t6;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
};
function _temp(state) {
  return state.canSubmit;
}